import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, COLORS, ICONS, Icon } from "abs-ui-components";
import { useSelector } from "react-redux";
import CustomTable from "../../../common/customTable/CustomTable";
import AbsModal from "../../../common/absModal/AbsModal";
import BrowseDocumentFile from "../../ReportDetails/Documents/BrowseDocumentFile";
import axiosHttp from "../../../services/interceptor";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { ClaimsContext } from "../../../services/ClaimsService";
import { ClaimType } from "../../../constants/claims";
import "../../ReportDetails/Documents/Documents.scss";

const UKETSDocuments = ({ ukEtsAerMstId }) => {
	const { userHasClaim } = useContext(ClaimsContext);
	const user = useSelector((state) => state.userInfo.userData);
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [documents, setDocuments] = useState([]);
	const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isUpdated, setIsUpdated] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);

	/*
	 * ---------------------------------------------------------
	 * Browse Files modal
	 * ---------------------------------------------------------
	 */

	const toggleDocumentModal = () => {
		setIsDocumentsModalOpen((prev) => !prev);

		if (isDocumentsModalOpen) {
			setSelectedFiles([]);
		}
	};

	/*
	 * ---------------------------------------------------------
	 * Fetch documents
	 * ---------------------------------------------------------
	 */

	useEffect(() => {
		if (!ukEtsAerMstId) return;

		fetchDocumentsData();
	}, [ukEtsAerMstId, isUpdated]);

	const fetchDocumentsData = async () => {
		setIsLoading(true);

		try {
			const response = await axiosHttp.get(
				`api/UkEtsCompanyReport/GetUkEtsCompanyReportDocumentsById?ukEtsCompanyLevelReportId=${ukEtsAerMstId}&isVerificationDocument=${false}`
			);

			if (response?.status === 200) {
				const responseData = response?.data || [];
				const documentList = Array.isArray(response.data) ? response.data : [];
				setDocuments(documentList);
				const availableDocumentIds = new Set(documentList.map((doc) => doc.documentId));
			} else {
				setDocuments([]);
			}
		} catch (error) {
			console.error("Error fetching UK ETS documents:", error);

			emitToasterMsg("error", "Documents", "Unable to load documents.");
		} finally {
			setIsLoading(false);
		}
	};

	/*
	 * ---------------------------------------------------------
	 * Upload
	 * ---------------------------------------------------------
	 */

	const uploadDocuments = async (files) => {
		if (!files?.length) {
			return;
		}

		const formData = new FormData();

		files.forEach((file) => {
			formData.append("DocumentFiles", file);
		});

		formData.append("UkEtsCompanyLevelReportId", ukEtsAerMstId);

		formData.append("IsVerification", false);

		setIsLoading(true);

		try {
			const response = await axiosHttp.post(`api/UkEtsCompanyReport/UploadUkEtsSupportingDocuments`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			if (response?.data?.isSuccess !== false) {
				emitToasterMsg("success", "Documents", "File(s) Uploaded Successfully");

				setSelectedFiles([]);
				setIsDocumentsModalOpen(false);

				setIsUpdated((previous) => previous + 1);
			} else {
				emitToasterMsg("error", "Documents", response?.data?.errorMessage || "Unable to upload documents.");
			}
		} catch (error) {
			console.error("Error uploading UK ETS documents:", error);

			emitToasterMsg("error", "Documents", "Error uploading files.");
		} finally {
			setIsLoading(false);
		}
	};

	const handleUploadClick = () => {
		uploadDocuments(selectedFiles);
	};

	/*
	 * ---------------------------------------------------------
	 * Individual download
	 * ---------------------------------------------------------
	 */

	const handleDownload = async (documentId, documentName) => {
		setIsLoading(true);

		try {
			const response = await axiosHttp.get(`api/UkEtsCompanyReport/download-doc?ukEtsCompanyReportId=${ukEtsAerMstId}&documentId=${documentId}`, {
				responseType: "blob",
			});

			const url = window.URL.createObjectURL(new Blob([response.data]));

			const link = document.createElement("a");

			link.href = url;
			link.setAttribute("download", documentName);

			document.body.appendChild(link);

			link.click();

			link.remove();
			window.URL.revokeObjectURL(url);

			emitToasterMsg("success", "Documents", "Document Downloaded Successfully");
		} catch (error) {
			console.error("Error downloading UK ETS document:", error);

			emitToasterMsg("error", "Documents", "Unable to download document.");
		} finally {
			setIsLoading(false);
		}
	};

	/*
	 * ---------------------------------------------------------
	 * Download all
	 * ---------------------------------------------------------
	 */

	const handleDownloadAll = async () => {
		setIsLoading(true);

		try {
			const response = await axiosHttp.get(`api/UkEtsCompanyReport/DownloadAllUkEtsSupportingDocumentsById?ukEtsCompanyLevelReportId=${ukEtsAerMstId}`, {
				responseType: "blob",
			});

			const url = window.URL.createObjectURL(new Blob([response.data]));

			const link = document.createElement("a");

			link.href = url;
			link.setAttribute("download", "UKETS Annual Emission Supporting Docs.zip");

			document.body.appendChild(link);

			link.click();

			link.remove();
			window.URL.revokeObjectURL(url);

			emitToasterMsg("success", "Documents", "All Documents Downloaded Successfully");
		} catch (error) {
			console.error("Error downloading all UK ETS documents:", error);

			emitToasterMsg("error", "Documents", "Unable to download documents.");
		} finally {
			setIsLoading(false);
		}
	};

	/*
	 * ---------------------------------------------------------
	 * Delete
	 * ---------------------------------------------------------
	 */

	const handleDeleteDocument = async (documentId) => {
		try {
			const response = await axiosHttp.delete(`api/UkEtsCompanyReport/delete-doc?ukEtsCompanyReportId=${ukEtsAerMstId}&documentId=${documentId}`, {
				responseType: "blob",
			});

			if (response?.status === 200) {
				emitToasterMsg("success", "Documents", response?.errorMessage || "Document deleted successfully.");

				setIsUpdated((previous) => previous + 1);
			} else {
				emitToasterMsg("error", "Documents", response?.errorMessage || "Unable to delete document.");
			}
		} catch (error) {
			console.error("Error while deleting UK ETS document:", error);

			emitToasterMsg("error", "Documents", "Unable to delete document.");
		}
	};

	/*
	 * ---------------------------------------------------------
	 * Table
	 * ---------------------------------------------------------
	 */

	const documentsColumns = useMemo(
		() => [
			{
				header: "Document",
				accessorKey: "documentFileName",
				enableSorting: false,
				enableColumnActions: false,
				enableColumnFilters: false,
				size: 500,
			},
			{
				accessorKey: "action",
				header: "Download",
				enableSorting: false,
				enableColumnActions: false,
				enableColumnFilters: false,
				size: 150,

				Cell: ({ row: { original } }) =>
					documents.length > 0 ? (
						<Button onClick={() => handleDownload(original.documentId, original.documentFileName)}>
							<Icon icon={ICONS.DOWNLOAD} />
						</Button>
					) : null,

				Footer: () => (documents.length > 0 ? <Button onClick={handleDownloadAll}>Download All</Button> : null),
			},
			...(userHasClaim(ClaimType.DELETE_DOCUMENT)
				? [
						{
							accessorKey: "deleteAction",
							header: "Delete",
							enableSorting: false,
							enableColumnActions: false,
							enableColumnFilters: false,
							size: 150,

							Cell: ({ row: { original } }) =>
								documents.length > 0 ? (
									<Button
										primaryColor={COLORS.RED.DARK}
										primaryBackgroundColor={COLORS.RED.LIGHT}
										onClick={() => handleDeleteDocument(original.documentId)}
									>
										Delete
									</Button>
								) : null,
						},
					]
				: []),
		],
		[documents, userHasClaim]
	);

	return (
		<section>
			{/* Browse Files */}
			<div className="mb-4">
				<Button disabled={false} onClick={toggleDocumentModal}>
					Browse Files
				</Button>
			</div>

			{/* Documents Table */}
			<div className="browse-document-container">
				<CustomTable
					columnsData={documentsColumns}
					renderTableData={documents}
					customProps={{
						enableToptoolbar: false,
						handleExportTable: false,
						showExportButton: false,
						enableStickyFooter: true,
						columnResize: true,
						isLoading: isLoading || isSubmitting,
						enableColumnVirtualization: false,
						enableRowVirtualization: false,
						columnPinning: {
							left: ["documentFileName"],
							right: ["action", "deleteAction"],
						},
					}}
				/>

				{/* Browse Files Modal */}
				{isDocumentsModalOpen && (
					<AbsModal isOpen={isDocumentsModalOpen} title="Browse File" onClose={setIsDocumentsModalOpen} close={toggleDocumentModal}>
						<BrowseDocumentFile
							handleUploadClick={handleUploadClick}
							documentHeader={[]}
							toggleCloseModal={toggleDocumentModal}
							selectedFiles={selectedFiles}
							setSelectedFiles={setSelectedFiles}
							isDocumentsModalOpen={isDocumentsModalOpen}
						/>
					</AbsModal>
				)}
			</div>
		</section>
	);
};

export default UKETSDocuments;
